import React, { useState, useEffect, useRef } from "react";
import styles from "./UploadProgressPopup.module.css";
import { trimStr } from "../../utils/theme/ShortenedText/ShortenedText";
import { describeItem, barPercent } from "../../upload/uploadManager";

/**
 * Always-mounted upload tray.
 *
 * The point of this panel is that a client uploading a 30GB file can tell, at a glance
 * and at any moment, that something is still happening. Previously it showed a bare
 * percentage with no bytes, no speed and no ETA, and its ✕ button wiped the whole queue
 * while the transfers carried on invisibly — so people assumed failure and re-uploaded.
 * Now ✕ only collapses the tray; discarding an upload is an explicit per-item action.
 */

const PHASE_LABEL = {
  uploading: "Uploading",
  paused: "Paused",
  interrupted: "Interrupted",
  processing: "Transcoding",
  ready: "Done",
  failed: "Failed",
};

const UploadProgressPopup = ({
  uploadQueue,
  onPause,
  onResume,
  onCancel,
  onDismiss,
  onDismissFinished,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const popupRef = useRef(null);
  // One hidden input, retargeted per item: after a page reload the File handle is gone,
  // so resuming needs the user to point at the same file again.
  const resumeInputRef = useRef(null);
  const resumeTargetRef = useRef(null);

  const togglePopup = () => setCollapsed((prev) => !prev);

  useEffect(() => {
    if (!collapsed && popupRef.current) popupRef.current.scrollTop = 0;
  }, [collapsed]);

  // Re-open the tray whenever a NEW upload starts.
  //
  // ✕ collapses rather than clearing the queue (clearing orphaned running uploads, which
  // is what made people re-upload). But `collapsed` lives until the page reloads, so once
  // hidden it stayed hidden — every later upload ran with its progress invisible, looking
  // exactly like "no progress at all, then success". A new upload is new information, so
  // the tray must come back on its own.
  const knownIds = useRef(new Set());
  useEffect(() => {
    const ids = (uploadQueue || []).map((i) => i.videoId);
    const hasNew = ids.some((id) => !knownIds.current.has(id));
    knownIds.current = new Set(ids);
    if (hasNew) setCollapsed(false);
  }, [uploadQueue]);

  const isHidden = !uploadQueue || uploadQueue.length === 0;

  const activeCount = isHidden
    ? 0
    : uploadQueue.filter((i) => i.phase === "uploading" || i.phase === "processing").length;
  const needsAttention = isHidden
    ? 0
    : uploadQueue.filter((i) => i.phase === "interrupted" || i.phase === "failed").length;
  const hasFinished = !isHidden && uploadQueue.some((i) => i.phase === "ready" || i.phase === "failed");

  // Heading reflects what is actually happening rather than a single fixed label.
  let heading = "Uploads";
  if (!isHidden) {
    if (needsAttention > 0) {
      heading = `${needsAttention} need${needsAttention === 1 ? "s" : ""} attention`;
    } else if (uploadQueue.every((i) => i.phase === "ready")) {
      heading = "Completed";
    } else if (uploadQueue.every((i) => i.phase === "processing" || i.phase === "ready")) {
      heading = "Transcoding";
    } else if (activeCount > 0) {
      heading = `Uploading ${activeCount}`;
    }
  }

  const pickFileToResume = (videoId) => {
    resumeTargetRef.current = videoId;
    if (resumeInputRef.current) {
      resumeInputRef.current.value = "";
      resumeInputRef.current.click();
    }
  };

  const handleResumeFile = (e) => {
    const file = e.target.files?.[0];
    const videoId = resumeTargetRef.current;
    resumeTargetRef.current = null;
    if (file && videoId != null && typeof onResume === "function") {
      onResume(videoId, file);
    }
  };

  const confirmCancel = (item) => {
    const done = Math.round(barPercent(item));
    // Cancelling throws away real transferred bytes, so make it deliberate.
    const ok = window.confirm(
      `Cancel "${item.title}"?\n\n${done}% has already been uploaded and will be discarded.`
    );
    if (ok && typeof onCancel === "function") onCancel(item.videoId);
  };

  return (
    <div
      className={`${styles.popupWrapper} ${
        collapsed ? styles.collapsed : styles.expanded
      } ${isHidden ? styles.hidden : ""}`}
    >
      <input
        type="file"
        accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-matroska,video/x-msvideo"
        ref={resumeInputRef}
        style={{ display: "none" }}
        onChange={handleResumeFile}
      />

      {/* Collapsed tab — carries live progress so a hidden tray still says something is
          happening, instead of looking like the upload vanished. */}
      {collapsed && (
        <div className={styles.collapseTab} onClick={togglePopup} title="Show uploads">
          {(() => {
            const active = uploadQueue.filter(
              (i) => i.phase === "uploading" || i.phase === "processing"
            );
            if (active.length === 0) return `▲ ${uploadQueue.length}`;
            const pct = Math.round(barPercent(active[0]));
            const suffix = active.length > 1 ? ` +${active.length - 1}` : "";
            return `▲ ${pct}%${suffix}`;
          })()}
        </div>
      )}

      {/* Main popup */}
      <div className={styles.progressPopup} ref={popupRef}>
        <div className={styles.headingBox}>
          <p className={styles.progressHeading}>{heading}</p>
          <div>
            {hasFinished && (
              <button
                className={styles.clearBtn}
                onClick={onDismissFinished}
                title="Clear completed"
              >
                Clear
              </button>
            )}
            <button className={styles.minimizeBtn} onClick={togglePopup} title="Minimise">
              —
            </button>
            {/* Collapse, not clear: uploads must never be silently orphaned. */}
            <button className={styles.closeBtn} onClick={togglePopup} title="Hide">
              ✕
            </button>
          </div>
        </div>

        <div className={styles.contentArea}>
          {uploadQueue.map((item) => {
            const percent = Math.round(barPercent(item));
            const isActive = item.phase === "uploading";
            const isRecoverable = item.phase === "interrupted" || item.phase === "paused";
            const canCancel = ["uploading", "paused", "interrupted"].includes(item.phase);

            return (
              <div key={item.videoId} className={styles.progressItem}>
                <div className={styles.itemHeader}>
                  <span className={styles.progressTitle} title={item.title}>
                    {trimStr(item.title, 28)}
                  </span>
                  <span className={`${styles.phaseTag} ${styles[`phase_${item.phase}`] || ""}`}>
                    {PHASE_LABEL[item.phase] || item.phase}
                  </span>
                </div>

                <div className={styles.barRow}>
                  <div className={styles.progressBar}>
                    <div
                      className={`${styles.progressFill} ${
                        item.phase === "processing" ? styles.fillProcessing : ""
                      } ${item.phase === "failed" ? styles.fillFailed : ""} ${
                        isRecoverable ? styles.fillPaused : ""
                      }`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <span className={styles.progressPercentage}>{percent}%</span>
                </div>

                {/* Bytes / speed / ETA — the detail that tells a client the transfer is
                    alive, which is what stops duplicate uploads. */}
                <div className={styles.itemMeta}>{describeItem(item)}</div>

                <div className={styles.itemActions}>
                  {isActive && (
                    <button className={styles.linkBtn} onClick={() => onPause?.(item.videoId)}>
                      Pause
                    </button>
                  )}
                  {isRecoverable && (
                    <button
                      className={styles.linkBtn}
                      onClick={() =>
                        item.needsFile
                          ? pickFileToResume(item.videoId)
                          : onResume?.(item.videoId)
                      }
                    >
                      {item.needsFile ? "Select file & resume" : "Resume"}
                    </button>
                  )}
                  {canCancel && (
                    <button
                      className={`${styles.linkBtn} ${styles.dangerBtn}`}
                      onClick={() => confirmCancel(item)}
                    >
                      Cancel
                    </button>
                  )}
                  {(item.phase === "ready" || item.phase === "failed") && (
                    <button className={styles.linkBtn} onClick={() => onDismiss?.(item.videoId)}>
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UploadProgressPopup;
